import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useUISlice } from "@/models/useUISlice";
import { useRouter } from "@/_router";
import { SearchResultItem } from "@/types";
import { actions } from "@/controller";
import { SearchIcon, CloseIcon } from "./Icons";
import { setMetaThemeColor, resetMetaThemeColor } from "@/models/useTheme";
import { logClientEvent } from "@/telemetry";
import { attachSearchKeyboard } from "@/dom-events";

const AlertWidgetSearch = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const params = useUISlice((state) => state.alertWidgetSearch);
  const visible = Boolean(params);
  const setVisible = useUISlice((state) => state.setAlertWidgetSearch);

  // Simplified state: just query, results, loading
  const [query, setQuery] = useState("");
  const [lastSearchedQuery, setLastSearchedQuery] = useState("");
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Opened from a transcript page, search is scoped to that transcript.
  const scopedTranscriptId = params?.transcriptId;

  // Search function
  const search = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setLastSearchedQuery("");
        return;
      }

      setLoading(true);
      setLastSearchedQuery(searchQuery);
      try {
        const result = await actions.search.all(searchQuery);
        let collected = "error" in result ? [] : result.value;
        if (scopedTranscriptId) {
          collected = collected.filter(
            (item) => item.transcriptId === scopedTranscriptId
          );
        }

        setResults(collected);
        logClientEvent("search.completed", {
          resultsCount: collected.length,
        });
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [scopedTranscriptId]
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (visible) {
      setQuery("");
      setLastSearchedQuery("");
      setResults([]);
      setLoading(false);
      // Focus input after mount
      setTimeout(() => inputRef.current?.focus(), 50);
    }

    return () => {};
  }, [visible, setVisible]);

  // Handle escape key
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setVisible(undefined);
      }
      if (e.key === "Enter" && query.trim()) {
        search(query);
      }
    };

    return attachSearchKeyboard(document, handleKeyDown);
  }, [visible, setVisible, query, search]);

  // Update theme color
  useEffect(() => {
    if (!visible) return;

    // Use CSS variable for modal theme color
    const computedColor = getComputedStyle(document.documentElement)
      .getPropertyValue("--theme-color-modal")
      .trim();
    if (computedColor) {
      setMetaThemeColor(computedColor);
    }

    return () => {
      resetMetaThemeColor();
    };
  }, [visible]);

  const handleResultClick = (result: SearchResultItem) => {
    logClientEvent("search.resultSelected", {
      transcriptId: result.transcriptId,
    });
    setVisible(undefined);
    const urlParams = new URLSearchParams({
      id: result.transcriptId,
      q: query,
    });
    if (result.textStart >= 0) {
      urlParams.set("t", String(result.textStart));
    }
    router.push({
      pathname: "/transcript",
      search: `?${urlParams.toString()}`,
    });
  };

  const renderHighlight = (text: string) => {
    const parts = text.split(/(<mark>.*?<\/mark>)/);
    return parts.map((part, i) => {
      if (part.startsWith("<mark>") && part.endsWith("</mark>")) {
        const content = part.slice(6, -7);
        return (
          <span
            key={i}
            className="bg-[var(--tint-soft)] text-[var(--tint-strong)] rounded px-0.5 font-semibold"
          >
            {content}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        data-action-id="search.dismiss"
        className="scrim fixed inset-0 z-[1000] transition-all duration-300"
        onClick={() => setVisible(undefined)}
      />

      {/* Search Modal */}
      <div className="glass-thick fixed top-[15%] left-1/2 -translate-x-1/2 z-[1001] w-[calc(100%-32px)] max-w-[540px] rounded-[32px] overflow-hidden animate-in fade-in slide-in-from-top-4 duration-300">
        {/* Search Input Area */}
        <div className="flex items-center w-full px-6 py-5">
          <div className="shrink-0 w-6 flex items-center justify-center">
            <SearchIcon size={24} className="text-[var(--text-tertiary)]" />
          </div>
          <input
            data-action-id="search.query.change"
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              params?.placeholder || t("alertWidgetSearch.placeholder")
            }
            className="flex-1 min-w-0 bg-transparent text-[var(--text-primary)] placeholder:text-[var(--text-muted)] px-4 py-2 outline-none text-xl font-medium tracking-tight"
          />
          {query ? (
            <button
              data-action-id="search.query.clear"
              onClick={() => {
                setQuery("");
                setLastSearchedQuery("");
              }}
              className="icon-btn icon-btn-quiet shrink-0"
            >
              <CloseIcon size={20} />
            </button>
          ) : (
            <div className="shrink-0 w-9" />
          )}
        </div>

        {/* Results Container */}
        <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
          {/* Action: Search For ... */}
          {!loading && query.trim() && query !== lastSearchedQuery && (
            <div className="p-2">
              <button
                data-action-id="search.submit"
                onClick={() => search(query)}
                className="w-full text-start px-5 py-6 rounded-3xl bg-[var(--fill-1)] hover:bg-[var(--fill-2)] transition-colors active:scale-[0.98] group flex items-center gap-5"
              >
                <div className="w-12 h-12 rounded-full bg-[var(--tint-soft)] flex items-center justify-center shrink-0">
                  <SearchIcon size={22} className="text-[var(--tint)]" />
                </div>
                <div>
                  <p className="text-[var(--text-primary)] font-semibold text-xl">
                    {t("alertWidgetSearch.searchFor", { query })}
                  </p>
                  <p className="text-[var(--text-secondary)] text-base mt-0.5">
                    {t("alertWidgetSearch.tapToView")}
                  </p>
                </div>
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12 gap-3">
              <div className="w-6 h-6 border-2 border-[var(--fill-3)] border-t-[var(--text-primary)] rounded-full animate-spin" />
            </div>
          )}

          {/* Results */}
          {!loading && query === lastSearchedQuery && results.length > 0 && (
            <div className="px-2 pb-2 flex flex-col gap-1">
              {results.map((result, index) => (
                <button
                  key={`${result.transcriptId}:${result.textStart}:${index}`}
                  data-action-id="search.result.open"
                  onClick={() => handleResultClick(result)}
                  className="w-full text-start px-5 py-4 rounded-2xl hover:bg-[var(--fill-1)] transition-all active:scale-[0.98] group"
                >
                  <p className="text-[var(--text-primary)] font-semibold text-lg truncate transition-colors">
                    {renderHighlight(result.transcriptName)}
                  </p>
                  <p className="text-[var(--text-secondary)] text-sm mt-1.5 line-clamp-2 leading-relaxed font-normal">
                    {renderHighlight(result.text)}
                  </p>
                </button>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && query === lastSearchedQuery && results.length === 0 && (
            <div className="w-full flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="bg-[var(--fill-1)] p-4 rounded-full mb-4">
                <SearchIcon size={32} className="text-[var(--text-tertiary)]" />
              </div>
              <p className="text-[var(--text-secondary)] font-medium">
                {t("alertWidgetSearch.noResults")}
              </p>
            </div>
          )}

          {/* Initial State - Clean/Empty or Recent (could be added later) */}
          {!loading && !query.trim() && <div className="py-12" />}
        </div>
      </div>
    </>
  );
};

export default AlertWidgetSearch;
